<template>
  <div class="flex flex-col">
    <div class="sticky top-0 z-[10] bg-white">
      <div class="p-4 border-b border-gray-200">
        <div class="flex justify-between items-center">
          <h3 class="text-sm font-medium text-gray-700">
            체결된 호가 ({{ asks.length }}건)
          </h3>
          <div class="flex items-center gap-4">
            <div class="flex items-center gap-2">
              <select v-model="sortBy" class="text-sm border rounded-md px-2 py-1">
                <option value="maturity">만기순</option>
                <option value="yield">민평순</option>
              </select>
            </div>
            <button @click="handleCopy" class="text-gray-600 hover:text-gray-900 flex items-center gap-2">
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <span v-if="copySuccess" class="text-sm text-blue-600">복사 완료</span>
              <span v-else class="text-sm">복사</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 통합된 테이블 -->
    <div class="overflow-hidden">
      <table class="min-w-full divide-y divide-gray-200" style="table-layout: fixed">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              내용
            </th>
            <th v-if="isLowCouponFilterActive"
              class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
              쿠폰금리
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
              민평
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
              스프레드
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
              체결금리
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
              지표스프레드
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
              잔존만기
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-if="asks.length === 0">
            <td :colspan="isLowCouponFilterActive ? 7 : 6" class="px-4 py-8 text-sm text-center text-gray-500">
              체결된 호가가 없습니다
            </td>
          </tr>
          <template v-for="ask in sortedAsks" :key="ask.id">
            <tr class="hover:bg-gray-50 cursor-pointer transition-colors duration-200" @click="toggleDetails(ask.id)"
              :class="{ 'bg-blue-50': expandedRows.includes(ask.id) }">
              <td class="px-4 py-3 text-sm text-gray-600 min-w-0">
                <div class="flex items-center gap-2" :title="ask.content">
                  <svg class="w-4 h-4 flex-shrink-0" :class="{ 'rotate-180': expandedRows.includes(ask.id) }"
                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                  <span class="block">{{ ask.content }}</span>

                  <!-- 라벨 표시 (content 바로 옆) -->
                  <div class="flex items-center gap-1 flex-shrink-0 relative" @click.stop>
                    <!-- 적용된 라벨들 (클릭 시 라벨 선택 팝업) -->
                    <div v-for="label in getVisibleLabels(ask.id)" :key="label.id"
                      @click.stop="toggleLabelSelector(ask.id)"
                      class="w-3 h-3 rounded-full flex-shrink-0 cursor-pointer hover:scale-125 transition-transform"
                      :style="{ backgroundColor: label.colorCode }" :title="label.name"></div>

                    <!-- 추가 라벨 개수 표시 (클릭 시 라벨 선택 팝업) -->
                    <span v-if="getExtraLabelCount(ask.id) > 0" @click.stop="toggleLabelSelector(ask.id)"
                      class="text-xs text-gray-500 cursor-pointer hover:text-gray-700"
                      :title="getExtraLabelNames(ask.id)">
                      +{{ getExtraLabelCount(ask.id) }}
                    </span>

                    <!-- 라벨 추가 버튼 -->
                    <button @click.stop="toggleLabelSelector(ask.id)"
                      class="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-500 transition-colors"
                      title="라벨 추가">
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                      </svg>
                    </button>
                    <LabelSelector v-model="openLabelSelector[ask.id]" :credit-quote-id="ask.id" position="bottom-right"
                      @label-toggled="handleLabelToggled(ask.id)" />
                  </div>
                </div>
              </td>
              <td v-if="isLowCouponFilterActive" class="px-4 py-3 text-sm whitespace-nowrap text-left">
                <span>{{ formatYield(ask.couponRate) || '-' }}</span>
              </td>
              <td class="px-4 py-3 text-sm whitespace-nowrap text-left">
                {{ formatYield(ask.yield) }}
              </td>
              <td class="px-4 py-3 text-sm whitespace-nowrap text-left">
                <div class="flex items-center gap-1">
                  <div class="w-2 flex-shrink-0">
                    <span v-if="isNonZeroSpread(ask)" :class="{
                      'bg-blue-500': isUnderOneYear(ask.maturityDate)
                        ? ask.spreadPrice < 0
                        : ask.spreadYield > 0,
                      'bg-red-500': isUnderOneYear(ask.maturityDate)
                        ? ask.spreadPrice > 0
                        : ask.spreadYield < 0,
                    }" class="inline-block w-2 h-2 rounded-full"></span>
                  </div>
                  <span>{{ formatSpreadValue(ask) }}</span>
                  <Tooltip v-if="ask.hasWidePriceGap" text="거래내역을 확인해주세요" position="top" width="142">
                    <span class="text-red-500 bg-red-100 rounded-full px-2">!</span>
                  </Tooltip>
                </div>
              </td>
              <td class="px-4 py-3 text-sm whitespace-nowrap text-left">
                {{ formatYield(ask.representationTradingYield) }}
              </td>
              <td class="px-4 py-3 text-sm whitespace-nowrap text-left">
                <!-- 1년 미만 채권: 완전히 비어있음 -->
                <div v-if="isUnderOneYear(ask.maturityDate)">
                  <!-- 빈 공간 -->
                </div>
                <!-- 1년 이상 채권: 지표스프레드 표시 -->
                <div v-else>
                  <div v-if="ask.hasBenchmarkTx && ask.benchmarkBondType && ask.benchmarkMaturityRangeType"
                    class="flex items-center gap-1">
                    <div class="w-2 flex-shrink-0">
                      <span v-if="ask.benchmarkVsSpread && parseFloat(ask.benchmarkVsSpread) !== 0" :class="{
                        'bg-blue-500': parseFloat(ask.benchmarkVsSpread) > 0,
                        'bg-red-500': parseFloat(ask.benchmarkVsSpread) < 0,
                      }" class="inline-block w-2 h-2 rounded-full"></span>
                    </div>
                    <Tooltip :text="getBenchmarkSpreadText(ask)" position="top" width="180">
                      <span>{{ formatBenchmarkVsSpread(ask) }}</span>
                    </Tooltip>
                  </div>
                  <div v-else>
                    <Tooltip text="비교 가능한 지표 거래 없음" position="top" width="200">
                      <span class="text-gray-400">-</span>
                    </Tooltip>
                  </div>
                </div>
              </td>
              <td class="px-4 py-3 text-sm whitespace-nowrap text-left text-gray-500">
                {{ formatRemainingMaturity(ask.maturityDate) }}
              </td>
            </tr>
            <tr v-if="expandedRows.includes(ask.id)">
              <td :colspan="isLowCouponFilterActive ? 7 : 6" class="px-4 py-2 bg-gray-50">
                <AskDetailComponent :ask="ask" :transactions="expandedTransactions[ask.id] || []" />
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { format, differenceInMonths } from 'date-fns';
import Tooltip from '@/components/common/Tooltip.vue';
import AskDetailComponent from './AskDetailComponent.vue';
import { useStore } from 'vuex';
import askService from '@/service/creditQuoteService';
import LabelSelector from '@/components/common/LabelSelector.vue';
import { useQuoteLabel } from '@/composables/useQuoteLabel';
export default {
  name: 'SoldOutAsksComponent',
  components: {
    Tooltip,
    AskDetailComponent,
    LabelSelector,
  },
  props: {
    asks: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  setup(props) {
    const { setQuoteLabelIds, getQuoteLabels } = useQuoteLabel();

    const sortBy = ref('maturity');
    const copySuccess = ref(false);
    const expandedRows = ref([]);
    const expandedTransactions = ref({});
    const openLabelSelector = ref({});
    const store = useStore();
    const isLowCouponFilterActive = computed(
      () => store.state.filter?.isLowCouponActive || false
    );

    const sortedAsks = computed(() => {
      let sorted = [...props.asks];
      switch (sortBy.value) {
        case 'yield':
          sorted.sort((a, b) => parseFloat(a.yield) - parseFloat(b.yield));
          break;
        default: // maturity
          sorted.sort(
            (a, b) => new Date(a.maturityDate) - new Date(b.maturityDate)
          );
      }
      return sorted;
    });

    const formatSpreadValue = ask => {
      if (isUnderOneYear(ask.maturityDate)) {
        return formatWon(ask.spreadPrice);
      }
      return formatSpread(ask.spreadYield);
    };

    const isNonZeroSpread = ask => {
      if (isUnderOneYear(ask.maturityDate)) {
        return ask.spreadPrice !== 0;
      }
      return parseFloat(ask.spreadYield) !== 0;
    };

    const toggleDetails = async id => {
      const index = expandedRows.value.indexOf(id);
      if (index === -1) {
        try {
          const transactions = await askService.findCreditSoldOut(id);
          expandedTransactions.value[id] = transactions;
          expandedRows.value.push(id);
        } catch (error) {
          console.error('거래내역 조회 실패:', error);
        }
      } else {
        expandedRows.value.splice(index, 1);
        delete expandedTransactions.value[id];
      }
    };

    const handleCopy = async () => {
      try {
        const text = sortedAsks.value.map(ask => ask.content).join('\n');

        await navigator.clipboard.writeText(text);
        copySuccess.value = true;
        setTimeout(() => {
          copySuccess.value = false;
        }, 2000);
      } catch (err) {
        console.error('복사 실패:', err);
      }
    };

    const formatRemainingMaturity = date => {
      if (!date) return '-';
      const maturityDate = new Date(date);
      const today = new Date();
      const monthsDiff = differenceInMonths(maturityDate, today);
      const years = Math.floor(monthsDiff / 12);
      const months = monthsDiff % 12;

      if (years === 0) return `${months}개월`;
      if (months === 0) return `${years}년`;
      return `${years}년 ${months}개월`;
    };

    const formatDetailTime = datetime => {
      if (!datetime) return '-';
      return format(new Date(datetime), 'HH:mm:ss');
    };

    // 포맷팅 함수들
    const formatYield = yieldValue => {
      if (!yieldValue || yieldValue === '-') return '-';
      return parseFloat(yieldValue).toFixed(3);
    };

    const formatWon = price => {
      if (price === null || price === undefined || price === '-') return '-';
      // 100만원:1원 비율로 변환하여 소수점 둘째자리까지 표시
      const convertedPrice = price / 1000000;
      return `${convertedPrice > 0 ? '+' : ''}${convertedPrice.toFixed(2)}원`;
    };

    const formatSpread = spread => {
      if (!spread) return '0.0bp';
      const spreadValue = parseFloat(spread).toFixed(1);
      return `${spreadValue > 0 ? '+' : ''}${spreadValue}bp`;
    };

    const formatBenchmarkSpread = ask => {
      if (!ask.spreadYield) return '-';
      const spread = parseFloat(ask.spreadYield).toFixed(1);
      return `${spread > 0 ? '+' : ''}${spread}bp`;
    };

    const formatBenchmarkVsSpread = ask => {
      if (!ask.benchmarkVsSpread) return '-';
      const spread = parseFloat(ask.benchmarkVsSpread).toFixed(1);
      return `${spread > 0 ? '+' : ''}${spread}bp`;
    };

    const formatVolume = volume => {
      if (!volume) return '-';
      // 원화를 억 단위로 변환 (1억 = 100,000,000원)
      const volumeInEok = volume / 100000000;
      return `${volumeInEok.toFixed(1)}억`;
    };

    const isValidSpread = spreadBp => {
      if (!spreadBp || spreadBp === '-' || spreadBp === null) return false;
      const spread = parseFloat(spreadBp);
      return !isNaN(spread);
    };

    const isHighlightedTransaction = (transaction, transactions) => {
      const sorted = [...transactions].sort((a, b) => {
        if (b.tradingPrice !== a.tradingPrice) {
          return b.tradingPrice - a.tradingPrice;
        }
        return b.tradingYield - a.tradingYield;
      });

      return (
        transaction === sorted[0] || transaction === sorted[sorted.length - 1]
      );
    };

    const isUnderOneYear = maturityDate => {
      if (!maturityDate) return false;
      const monthsDiff = differenceInMonths(new Date(maturityDate), new Date());
      return monthsDiff <= 12;
    };

    const formatTradingPrice = price => {
      if (!price) return '-';
      return `${price}원`;
    };

    const sortedTransactions = transactions => {
      return [...transactions].sort(
        (a, b) =>
          new Date(a.transactionDateTime) - new Date(b.transactionDateTime)
      );
    };

    const getBenchmarkSpreadText = ask => {
      if (!ask.benchmarkBondType || !ask.benchmarkMaturityRangeType || ask.benchmarkVsSpread === null || ask.benchmarkVsSpread === undefined) return '';

      const spreadDirection = ask.benchmarkVsSpread >= 0 ? '오버' : '언더';
      const spreadValue = Math.abs(ask.benchmarkVsSpread);

      return `${ask.benchmarkBondType} ${ask.benchmarkMaturityRangeType} 대비 ${spreadDirection} ${spreadValue}bp 수준`;
    };



    const formatDate = date => {
      if (!date) return '-';
      return format(new Date(date), 'MMM d, yyyy');
    };

    // 라벨 관련 함수
    const MAX_VISIBLE_LABELS = 3;

    const getVisibleLabels = (creditQuoteId) => {
      const labels = getQuoteLabels(creditQuoteId);
      return labels.slice(0, MAX_VISIBLE_LABELS);
    };

    const getExtraLabelCount = (creditQuoteId) => {
      const labels = getQuoteLabels(creditQuoteId);
      return Math.max(0, labels.length - MAX_VISIBLE_LABELS);
    };

    const getExtraLabelNames = (creditQuoteId) => {
      const labels = getQuoteLabels(creditQuoteId);
      const extraLabels = labels.slice(MAX_VISIBLE_LABELS);
      return extraLabels.map(l => l.name).join(', ');
    };

    const toggleLabelSelector = (askId) => {
      openLabelSelector.value[askId] = !openLabelSelector.value[askId];
    };

    const handleLabelToggled = () => {
      // 라벨이 토글되면 낙관적 업데이트로 이미 캐시가 갱신됨
    };

    // 호가 데이터가 변경될 때마다 라벨 ID를 캐시에 저장
    watch(
      () => props.asks,
      (newAsks) => {
        for (const ask of newAsks) {
          setQuoteLabelIds(ask.id, ask.labelIds);
        }
      },
      { immediate: true, deep: true }
    );

    return {
      sortBy,
      copySuccess,
      expandedRows,
      sortedAsks,
      toggleDetails,
      handleCopy,
      formatRemainingMaturity,
      formatDetailTime,
      formatYield,
      formatWon,
      formatSpread,
      formatVolume,
      isValidSpread,
      isHighlightedTransaction,
      isUnderOneYear,
      formatTradingPrice,
      expandedTransactions,
      formatSpreadValue,
      isNonZeroSpread,
      sortedTransactions,
      formatBenchmarkSpread,
      formatBenchmarkVsSpread,
      getBenchmarkSpreadText,
      formatDate,
      isLowCouponFilterActive,
      openLabelSelector,
      getVisibleLabels,
      getExtraLabelCount,
      getExtraLabelNames,
      toggleLabelSelector,
      handleLabelToggled,
    };
  },
};
</script>

<style scoped>
.cursor-help {
  cursor: help;
}

/* 내용 셀의 최대 너비를 제한하고 말줄임표 처리를 위한 스타일 */
.truncate {
  @apply overflow-hidden text-ellipsis whitespace-nowrap w-full;
}

/* 테이블 레이아웃 관련 스타일 수정 */
table {
  table-layout: fixed;
  width: 100%;
}

/* 내용 셀 스타일 */
.truncate {
  @apply overflow-hidden text-ellipsis whitespace-nowrap w-full;
}

/* 고정 너비 컬럼들의 너비 최적화 */
th:not(:first-child),
td:not(:first-child) {
  min-width: var(--column-width);
}

/* 내용 컬럼이 남은 공간을 모두 사용하도록 설정 */
th:first-child,
td:first-child {
  width: auto;
}
</style>
